/**
 * 하위 화면 공통 껍데기 — 뒤로가기 + 제목. 한 화면에 한 일.
 * 윗줄은 앱 바(스크롤해도 붙어 있다 · 아이폰 노치 여백 포함) — 2026-09-27 「전문 앱처럼」.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth } from '../lib/driverAuth';
import { Icon } from './icons';

export default function Screen({
  title,
  back = '/',
  acting = false,
  children,
}: {
  title: string;
  back?: string;
  /** 차주가 「누구 것으로」를 고르는 화면인가(명세서·세금계산서·4종 신청 · #258). 차주가 아니면 아무것도 안 그린다. */
  acting?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <div className="min-h-full pb-10">
      <header className="sticky top-0 z-30 border-b border-line/70 bg-surface/95 pt-[var(--sat)] backdrop-blur">
        <div className="flex h-16 items-center gap-1 px-2">
          <button
            className="flex min-h-touch min-w-touch items-center justify-center rounded-full text-ink transition active:bg-canvas"
            aria-label="뒤로"
            onClick={() => navigate(back)}
          >
            <Icon name="chevronLeft" className="h-7 w-7" />
          </button>
          <h1 className="text-xl font-bold text-ink">{title}</h1>
        </div>
      </header>
      <div className="px-5 pt-5">
        {acting && <ActingPicker />}
        {children}
      </div>
    </div>
  );
}

/**
 * 「누구 것으로」(#258 이현숙 — 차가 2대 이상인 차주는 본인 차량 전체를 · 서류신청처럼 본인 대신 다른 기사를 골라).
 * 차주 차량에 편성된 관리기사B 만 나온다(서버 `driverFleet` 이 정하고 다시 확인한다). 고르면 화면이 그 기사 것으로 다시 뜬다.
 */
function ActingPicker() {
  const { driver, allocs, fleet, acting, setActing } = useDriverAuth();
  if (!fleet.length) return null;
  // 본인 칸에도 차량번호(10-30 way 통화 — 차주가 「내 차 다섯 대 명세가 안 보인다」: 고르기가 사람 이름만이라 차량 고르기로 안 읽혔다).
  const selfCars = [...new Set(allocs.map((a) => shortCar(a.car_no ?? '')).filter(Boolean))].join('·');
  const carCnt = new Set([...allocs.map((a) => a.car_no), ...fleet.flatMap((f) => f.cars.map((c) => c.car_no))].filter(Boolean)).size;
  const other = fleet.find((f) => f.driver_id === acting) ?? null;
  const carsOf = (f: (typeof fleet)[number]) => f.cars.map((c) => shortCar(c.car_no ?? '')).filter(Boolean).join('·');
  // 「내 배차」 차량 고르기와 같은 한 줄 드롭다운(10-30 way) — 차 번호 뒤 4자리 + 이름 · 괄호 예비 별칭은 「(예비)」.
  return (
    <div className={`mb-5 rounded-2xl px-4 py-2 ring-1 ${other ? 'bg-amber-50 ring-amber-200' : 'bg-surface ring-line'}`}>
      <label className="flex items-center gap-3">
        <span className="shrink-0 text-base font-semibold text-ink-soft">
          누구 것을 볼까요?{carCnt > 1 && <span className="text-brand-700"> {carCnt}대</span>}
        </span>
        <select
          className="min-h-touch min-w-0 flex-1 rounded-xl bg-canvas px-3 text-base font-semibold text-ink ring-1 ring-line"
          value={acting ?? ''}
          onChange={(e) => setActing(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{selfCars ? `${selfCars} ` : ''}본인</option>
          {fleet.map((f) => {
            const { base, reserve } = splitAlias(f.driver_name);
            return (
              <option key={f.driver_id} value={f.driver_id}>
                {carsOf(f) ? `${carsOf(f)} ` : ''}{base || '기사'}{reserve ? '(예비)' : ''}
              </option>
            );
          })}
        </select>
      </label>
      {other && (
        <p className="mt-2 break-keep text-base font-semibold text-amber-800">
          지금 {other.driver_name} 기사님 것을 보고 있습니다. 신청하면 {other.driver_name} 기사님 이름으로 들어갑니다.
        </p>
      )}
    </div>
  );
}

/** 차량번호 뒤 4자리(숫자로 끝날 때만). */
export const shortCar = (no: string) => (/[0-9]{4}$/.test(no) ? no.slice(-4) : no);

/** 「고진기(최찬영예비)」 → 이름 + 예비 여부. 괄호가 예비 표기가 아니면 이름 그대로. */
export function splitAlias(name: string | null | undefined): { base: string; reserve: boolean } {
  const m = /^(.*?)[ ]*[(]([^)]*)[)][ ]*$/.exec(name ?? '');
  if (!m || !/예비/.test(m[2] ?? '')) return { base: (name ?? '').trim(), reserve: false };
  return { base: (m[1] ?? '').trim(), reserve: true };
}

/** 목록이 빌 때. */
export function Empty({ children }: { children: ReactNode }) {
  return <p className="driver-card px-4 py-12 text-center text-lg text-ink-soft">{children}</p>;
}

/** 로딩. */
export function Loading() {
  return <p className="px-4 py-10 text-center text-lg text-ink-soft">불러오는 중…</p>;
}

/** 오류 배너. */
export function ErrorBanner({ children }: { children: ReactNode }) {
  return <p className="mb-4 rounded-xl bg-red-50 px-4 py-4 text-base text-red-700">{children}</p>;
}

/**
 * 「신청하기」 바로 위 오류 — 문구가 바뀔 때마다 그 자리로 화면을 옮긴다.
 * 11-03 이가빈 챗: 신청이 막혀도 문구가 긴 폼 맨 위에만 떠서 맨 아래 버튼을 누른 기사님은 「아무것도 안 뜬다」고 했다.
 */
export function SubmitError({ message }: { message: string | null }) {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (message) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, [message]);
  if (!message) return null;
  return <p ref={ref} role="alert" className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-4 text-lg font-semibold text-red-700">{message}</p>;
}

/**
 * 내역 두 화면(세금계산서 · 페이앱·카드·현금영수증) 사이 탭 — 홈 「내 내역보기」는 계산서 쪽으로 열린다(10-12 기사 요청).
 * 바꿔도 뒤로 가기는 홈으로(replace).
 */
export function HistoryTabs({ active }: { active: 'invoices' | 'orders' | 'dasu' }) {
  const navigate = useNavigate();
  const tabs = [
    { key: 'invoices', label: '세금계산서' },
    { key: 'orders', label: '페이앱·카드·현금영수증' },
    { key: 'dasu', label: '다수배분' },
  ] as const;
  return (
    <div className="mb-5 grid grid-cols-3 gap-1 rounded-xl bg-canvas p-1 ring-1 ring-line/70">
      {tabs.map((t) => (
        <button
          key={t.key}
          className={`min-h-touch break-keep rounded-lg px-2 text-base font-semibold leading-tight transition ${
            t.key === active ? 'bg-surface text-brand-700 shadow' : 'text-ink-soft active:bg-surface/60'
          }`}
          aria-pressed={t.key === active}
          onClick={() => t.key !== active && navigate(`/${t.key}`, { replace: true })}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/**
 * 입찰 두 화면(신청할 수 있는 입찰 · 내 신청 결과) 사이 탭 — 2026-10-21 way: 신청 결과는 입찰 쪽에 모으고
 * 홈 「내 배차」는 배정된 운행만 보인다. 바꿔도 뒤로 가기는 홈으로(replace).
 */
export function BidTabs({ active }: { active: 'bids' | 'my-bids' }) {
  const navigate = useNavigate();
  const tabs = [
    { key: 'bids', label: '신청할 수 있는 입찰' },
    { key: 'my-bids', label: '내 신청 결과' },
  ] as const;
  return (
    <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-canvas p-1 ring-1 ring-line/70">
      {tabs.map((t) => (
        <button
          key={t.key}
          className={`min-h-touch break-keep rounded-lg px-2 text-base font-semibold leading-tight transition ${
            t.key === active ? 'bg-surface text-brand-700 shadow' : 'text-ink-soft active:bg-surface/60'
          }`}
          aria-pressed={t.key === active}
          onClick={() => t.key !== active && navigate(`/${t.key}`, { replace: true })}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** 내역 목록 제목 — 신청 버튼 아래, 지난 신청이 여기 있다는 표시. */
export function HistoryHeading() {
  return <h2 className="mb-3 px-1 text-lg font-bold text-ink">내 신청 내역</h2>;
}
